import { useCallback, useMemo, useState } from "react";
import {
  completeAttachment,
  uploadWithPresignedUrl,
  requestPresignedUrl,
} from "../service";
import {
  StoredFile,
  PresignedClientOptions,
  PresignedClient,
  PresignedUploadOptions,
  UploadedFile,
} from "../types";
import { useFileStorageValidator } from "./useFileStorageValidator";
import { createFileKey } from "../utils/createFileKey";

/**
 * Hook for managing file uploads and deletions using presigned URLs.
 * Tracks upload/delete state per file using filename as the key.
 * By BE design, a single section will not have two files with the same name,
 * which ensures unique keys for state tracking.
 */
export const usePresignedClient = ({
  presignUrl,
  completeUrl,
  completeDeleteUrl,
}: PresignedClientOptions): PresignedClient => {
  const { validateFiles } = useFileStorageValidator();
  const [uploading, setUploading] = useState<Set<string>>(() => new Set());
  const [removing, setRemoving] = useState<Set<string>>(() => new Set());

  const upload = useCallback(
    async (
      fieldName: string,
      file: File,
      options: PresignedUploadOptions = {},
    ): Promise<UploadedFile> => {
      const key = createFileKey(fieldName, file.name);
      setUploading((prev) => new Set([...prev, key]));

      try {
        if (!options.skipValidation) {
          const validationError = await validateFiles([file]);
          if (validationError) {
            throw new Error(validationError);
          }
        }

        const presign = await requestPresignedUrl({
          presignUrl,
          fieldName,
          filename: file.name,
          operation: "put",
        });

        await uploadWithPresignedUrl(
          presign.presignedUrl,
          file,
          presign.contentType,
          presign.metadata,
          presign.createOnly,
        );
        const completionResponse = await completeAttachment({
          completionUrl: completeUrl,
          attachmentId: presign.attachmentId,
        });

        return {
          attachmentId: presign.attachmentId,
          filename: presign.filename,
          s3Key: presign.s3Key,
          metadata: presign.metadata,
          completionResponse,
        };
      } finally {
        setUploading((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [presignUrl, completeUrl, validateFiles],
  );

  const remove = useCallback(
    async (fieldName: string, file: StoredFile): Promise<unknown> => {
      const key = createFileKey(fieldName, file.filename);
      setRemoving((prev) => new Set([...prev, key]));

      try {
        return await completeAttachment({
          completionUrl: completeDeleteUrl,
          attachmentId: file.attachmentId,
        });
      } finally {
        setRemoving((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [completeDeleteUrl],
  );

  const isUploading = uploading.size > 0;
  const isRemoving = removing.size > 0;

  return useMemo(
    () => ({
      upload,
      remove,
      isUploading,
      isRemoving,
    }),
    [upload, remove, isUploading, isRemoving],
  );
};
